import { useState } from 'react';
import { formatCurrency } from '@eriko/shared-types';
import { LineChart } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';

const TOP_N_GROUPS = 5;

interface FlowItem {
  source: string;
  amount: number;
  dueDate: string | null;
}

interface Week {
  weekStart: string;
  weekEnd: string;
  inflows: number;
  outflows: number;
  netChange: number;
  endingBalance: number;
  inflowItems: FlowItem[];
  outflowItems: FlowItem[];
}

interface Data {
  type: string;
  startingBalance: number;
  weeks: Week[];
}

interface GroupedFlow {
  source: string;
  count: number;
  totalAmount: number;
  items: FlowItem[];
}

function groupBySource(items: FlowItem[]): GroupedFlow[] {
  const map = new Map<string, GroupedFlow>();
  for (const item of items) {
    const key = item.source.trim().toLowerCase();
    const existing = map.get(key);
    if (existing) {
      existing.count += 1;
      existing.totalAmount += item.amount;
      existing.items.push(item);
    } else {
      map.set(key, {
        source: item.source,
        count: 1,
        totalAmount: item.amount,
        items: [item],
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.totalAmount - a.totalAmount);
}

interface ForecastSummary {
  startingBalance: number;
  endingBalance: number;
  totalInflow: number;
  totalOutflow: number;
  lowestWeek: { index: number; endingBalance: number } | null;
}

function summarizeForecast(data: Data): ForecastSummary {
  const totalInflow = data.weeks.reduce((sum, w) => sum + w.inflows, 0);
  const totalOutflow = data.weeks.reduce((sum, w) => sum + w.outflows, 0);
  const lastWeek = data.weeks[data.weeks.length - 1];
  const endingBalance = lastWeek ? lastWeek.endingBalance : data.startingBalance;
  let lowestWeek: ForecastSummary['lowestWeek'] = null;
  data.weeks.forEach((w, i) => {
    const current = lowestWeek;
    if (current === null || w.endingBalance < current.endingBalance) {
      lowestWeek = { index: i, endingBalance: w.endingBalance };
    }
  });
  return {
    startingBalance: data.startingBalance,
    endingBalance,
    totalInflow,
    totalOutflow,
    lowestWeek,
  };
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function signedDollar(v: number): string {
  return `${v > 0 ? '+' : v < 0 ? '−' : ''}${formatCurrency(Math.abs(v), 2)}`;
}

function WeekTrigger({
  week,
  index,
  prevEndingBalance,
}: {
  week: Week;
  index: number;
  prevEndingBalance: number | null;
}) {
  const netClass =
    week.netChange > 0
      ? 'text-pos bg-pos-surface'
      : week.netChange < 0
        ? 'text-neg bg-neg-surface'
        : 'text-surface-500 bg-surface-50';

  let deltaArrow = '';
  if (prevEndingBalance !== null) {
    if (week.endingBalance > prevEndingBalance) deltaArrow = '▲';
    else if (week.endingBalance < prevEndingBalance) deltaArrow = '▼';
    else deltaArrow = '=';
  }

  return (
    <>
      <span className="text-[13px] font-medium text-surface-700">
        Week {index + 1} · {shortDate(week.weekStart)} – {shortDate(week.weekEnd)}
      </span>
      <span
        className={`rounded-full px-1.5 py-0.5 text-[11px] font-medium font-mono tabular-nums ${netClass}`}
      >
        {signedDollar(week.netChange)}
        {deltaArrow && <span className="ml-1">{deltaArrow}</span>}
      </span>
      <span className="ml-auto shrink-0 font-mono text-[13px] tabular-nums text-surface-500">
        → {formatCurrency(week.endingBalance, 2)}
      </span>
    </>
  );
}

function FlowSection({
  direction,
  total,
  items,
  weekIndex,
}: {
  direction: 'in' | 'out';
  total: number;
  items: FlowItem[];
  weekIndex: number;
}) {
  const [showAllGroups, setShowAllGroups] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const heading = direction === 'in' ? 'Inflows' : 'Outflows';
  const headingColor = direction === 'in' ? 'text-pos' : 'text-neg';
  const emptyLabel =
    direction === 'in' ? 'No expected inflows.' : 'No expected outflows.';

  if (items.length === 0) {
    return (
      <section>
        <h4
          className={`mb-1 text-[10px] font-semibold uppercase tracking-[0.04em] ${headingColor}`}
        >
          {heading} {formatCurrency(total, 2)}
        </h4>
        <div className="text-xs text-surface-500">{emptyLabel}</div>
      </section>
    );
  }

  const groups = groupBySource(items);
  const visibleGroups = showAllGroups ? groups : groups.slice(0, TOP_N_GROUPS);
  const hiddenCount = groups.length - visibleGroups.length;

  function toggle(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <section>
      <h4
        className={`mb-1 text-[10px] font-semibold uppercase tracking-[0.04em] ${headingColor}`}
      >
        {heading} {formatCurrency(total, 2)}
      </h4>
      <ul className="space-y-1">
        {visibleGroups.map((g) => {
          const key = `${weekIndex}:${direction}:${g.source.trim().toLowerCase()}`;
          const isExpandable = g.count > 1;
          const isOpen = expanded.has(key);
          const firstItem = g.items[0];

          return (
            <li key={key}>
              <button
                type="button"
                onClick={isExpandable ? () => toggle(key) : undefined}
                className={`grid w-full grid-cols-[1fr_auto_auto] items-center gap-x-3 text-left text-xs ${
                  isExpandable ? 'cursor-pointer hover:bg-surface-50' : 'cursor-default'
                } rounded px-1 py-0.5`}
                aria-expanded={isExpandable ? isOpen : undefined}
              >
                <span className="truncate text-surface-700" title={g.source}>
                  {g.source}
                </span>
                <span className="w-20 text-right text-[11px] tabular-nums text-surface-500">
                  {g.count > 1 ? `×${g.count} items` : (firstItem?.dueDate ?? '—')}
                </span>
                <span className="w-24 text-right font-mono tabular-nums text-surface-900">
                  {formatCurrency(g.totalAmount, 2)}
                </span>
              </button>
              {isExpandable && isOpen && (
                <ul className="ml-3 mt-1 space-y-0.5 border-l border-surface-100 pl-2">
                  {g.items.map((it, idx) => (
                    <li
                      key={idx}
                      className="grid grid-cols-[1fr_auto] items-center gap-x-3 text-[11px] text-surface-500"
                    >
                      <span className="truncate tabular-nums">{it.dueDate ?? '—'}</span>
                      <span className="w-24 text-right font-mono tabular-nums text-surface-700">
                        {formatCurrency(it.amount, 2)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setShowAllGroups(true)}
          className="mt-1 text-[11px] font-medium text-surface-500 hover:text-surface-700"
        >
          Show {hiddenCount} more
        </button>
      )}
      {showAllGroups && groups.length > TOP_N_GROUPS && (
        <button
          type="button"
          onClick={() => setShowAllGroups(false)}
          className="mt-1 ml-3 text-[11px] font-medium text-surface-500 hover:text-surface-700"
        >
          Show less
        </button>
      )}
    </section>
  );
}

function SummaryStrip({ summary }: { summary: ForecastSummary }) {
  const { startingBalance, endingBalance, totalInflow, totalOutflow, lowestWeek } = summary;
  const lowestIsBelowStart = lowestWeek !== null && lowestWeek.endingBalance < startingBalance;

  return (
    <div className="border-b border-surface-100 px-4.5 py-3">
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-[0.04em] text-surface-500">
            Starting balance
          </div>
          <div className="mt-1 font-mono text-sm font-semibold tabular-nums text-surface-900">
            {formatCurrency(startingBalance, 2)}
          </div>
        </div>
        <div>
          <div className="text-[11px] font-medium uppercase tracking-[0.04em] text-surface-500">
            Ending balance
          </div>
          <div className="mt-1 font-mono text-sm font-semibold tabular-nums text-surface-900">
            {formatCurrency(endingBalance, 2)}
          </div>
        </div>
        <div>
          <div className="text-[11px] font-medium uppercase tracking-[0.04em] text-surface-500">
            Total inflows
          </div>
          <div className="mt-1 font-mono text-sm font-semibold tabular-nums text-pos">
            {formatCurrency(totalInflow, 2)}
          </div>
        </div>
        <div>
          <div className="text-[11px] font-medium uppercase tracking-[0.04em] text-surface-500">
            Total outflows
          </div>
          <div className="mt-1 font-mono text-sm font-semibold tabular-nums text-neg">
            {formatCurrency(totalOutflow, 2)}
          </div>
        </div>
      </div>
      {lowestWeek !== null && (
        <div
          className={`mt-2 text-[11px] ${lowestIsBelowStart ? 'text-neg' : 'text-surface-500'}`}
        >
          Lowest week: Week {lowestWeek.index + 1} —{' '}
          <span className="font-mono tabular-nums">
            {formatCurrency(lowestWeek.endingBalance, 2)}
          </span>
        </div>
      )}
    </div>
  );
}

export function CashflowForecastReport({
  data,
}: {
  data: Record<string, unknown>;
}) {
  const d = data as unknown as Data;
  const summary = summarizeForecast(d);

  return (
    <div className="overflow-hidden rounded-[10px] border border-surface-200 bg-surface-0 transition-shadow hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-surface-100 px-4.5 py-3.5">
        <div className="flex items-center gap-2.5">
          <LineChart
            className="h-4.25 w-4.25 text-surface-400"
            strokeWidth={1.5}
          />
          <span className="text-sm font-semibold text-surface-900">
            Cash Flow Forecast
          </span>
        </div>
        <span className="rounded-full bg-surface-50 px-2.5 py-0.75 text-xs font-medium text-surface-500">
          {d.weeks.length}-week forecast
        </span>
      </div>

      <SummaryStrip summary={summary} />

      {/* Per-week accordion */}
      <Accordion type="multiple">
        {d.weeks.map((w, i) => {
          const prevEndingBalance =
            i === 0 ? d.startingBalance : (d.weeks[i - 1]?.endingBalance ?? d.startingBalance);

          return (
            <AccordionItem
              key={i}
              value={`w${i}`}
              className="border-b border-surface-100 last:border-b-0"
            >
              <AccordionTrigger className="flex w-full items-center gap-3 px-4.5 py-2.5 text-left select-none transition-colors hover:bg-surface-50 hover:no-underline">
                <WeekTrigger week={w} index={i} prevEndingBalance={prevEndingBalance} />
              </AccordionTrigger>

              <AccordionContent className="pb-2 pt-1">
                <div className="grid grid-cols-2 gap-4 px-4.5">
                  <FlowSection
                    direction="in"
                    total={w.inflows}
                    items={w.inflowItems}
                    weekIndex={i}
                  />
                  <FlowSection
                    direction="out"
                    total={w.outflows}
                    items={w.outflowItems}
                    weekIndex={i}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}
